import { Close } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material'
import dayjs from 'dayjs'
import React from 'react'
import { formatAmountInCents } from '../../../domain/Adopters/GuestAdoption'
import { GuestAdoptionLinkDto } from '../../../interactors/clients/GuestAdoptionsClient'
import { theme } from '../../theme'
import { GuestAdoptionChip } from './GuestAdoptionChip'
import { GuestCheckoutLink } from './GuestCheckoutLink'

interface Props {
  link: GuestAdoptionLinkDto
  /** Deposit already collected from the adopter, in euros. Deducted from what is left to pay. */
  collectedDeposit?: number
  /** Called whichever way the view is dismissed, so the caller can open the procedure. */
  onDone: () => void
}

/**
 * Shown right after an adoption without documents was created: the association hands the link to
 * the adopter, who pays without creating an account. It replaces the former procedure email for
 * these adoptions, so the association sees what the adopter received and can pass it on another
 * way — by SMS or on the spot.
 */
export const GuestAdoptionLinkContent: React.FC<Props> = ({ link, collectedDeposit, onDone }) => {
  const depositInCents = Math.round((collectedDeposit || 0) * 100)
  const amountDueInCents = Math.max(link.amountInCents - depositInCents, 0)

  return (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Procédure d&apos;adoption créée
          </Typography>
          <GuestAdoptionChip sx={{ alignSelf: 'flex-start' }} />
          <Alert severity={link.emailSent ? 'success' : 'info'}>
            {link.emailSent
              ? `Le lien de paiement a été envoyé par email à l'adoptant. Vous pouvez aussi le lui transmettre vous-même.`
              : `Aucun email n'a été envoyé : transmettez vous-même le lien à l'adoptant (SMS, WhatsApp, sur place...).`}
          </Alert>
          <GuestCheckoutLink checkoutUrl={link.checkoutUrl} />
          <Typography variant="body2">
            {`Montant à régler par l'adoptant : `}
            <b>{formatAmountInCents(amountDueInCents)}</b>
            {depositInCents > 0 && ` (acompte de ${formatAmountInCents(depositInCents)} déjà encaissé déduit)`}
            {` · Lien valable jusqu'au ${dayjs(link.expiresAt).format('DD/MM/YYYY')}`}
          </Typography>
          <Typography variant="body2" fontStyle="italic">
            La procédure se clôturera automatiquement à la réception du paiement. Comme pour une adoption classique,
            pensez à mettre à jour la fiche de l&apos;animal (statut, date de sortie) lorsqu&apos;il quitte
            l&apos;association.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onDone} sx={{ textTransform: 'none' }}>
          Voir l&apos;adoption
        </Button>
      </DialogActions>
    </>
  )
}

/** Same view, for the screens that are not already inside a dialog of their own. */
export const GuestAdoptionLinkDialog: React.FC<Props> = (props) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      open
      onClose={props.onDone}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { borderRadius: 15, padding: isMobile ? 5 : 15 } }}
    >
      <IconButton sx={{ position: 'absolute', top: 10, right: 10 }} onClick={props.onDone}>
        <Close />
      </IconButton>
      <GuestAdoptionLinkContent {...props} />
    </Dialog>
  )
}
